import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Check, X, Trash, Eye, Star, CheckCircle } from '@phosphor-icons/react';
import { getAdminReviews, approveReview, rejectReview, deleteReview } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { toast } from 'sonner';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, approved

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: 10,
        ...(filter !== 'all' && { status: filter }),
        ...(search && { search }),
      };
      const res = await getAdminReviews(params);
      const reviewsData = res.data.data || [];
      console.log('Reviews data:', reviewsData.map(r => ({ id: r.id, status: r.status })));
      setReviews(reviewsData);
      setPagination(prev => ({
        ...prev,
        totalPages: res.data.pagination?.total_pages || 1,
        total: res.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [pagination.page, filter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== undefined) {
        setPagination(p => ({ ...p, page: 1 }));
        fetchReviews();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleApprove = async (id) => {
    try {
      await approveReview(id, true);
      toast.success('Review approved successfully');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectReview(id);
      toast.success('Review rejected successfully');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to reject review');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await deleteReview(id);
      toast.success('Review deleted successfully');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const getStatusBadge = (review) => {
    if (review.is_approved) {
      return (
        <div className="flex items-center gap-2">
          <span className="font-body text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Approved</span>
          {review.is_verified && (
            <span className="font-body text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Verified</span>
          )}
        </div>
      );
    }
    return <span className="font-body text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Pending</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">Reviews Management</h1>
          <p className="font-body text-sm text-text-secondary">{pagination.total} total reviews</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search reviews..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm"
            />
          </div>
          
          {/* Filter */}
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            className="px-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm bg-white"
          >
            <option value="all">All Reviews</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-background-subtle">
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Product</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Customer</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Rating</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Review</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Status</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Date</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-border-default">
                    <td colSpan={7} className="py-4 px-4">
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </td>
                  </tr>
                ))
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <p className="font-body text-text-secondary">No reviews found</p>
                  </td>
                </tr>
              ) : (
                reviews.map((review) => (
                  <motion.tr
                    key={review.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-border-default hover:bg-background-subtle transition-colors"
                  >
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-body text-sm text-secondary font-medium">{review.item_name || 'Unknown Product'}</p>
                        <p className="font-body text-xs text-text-muted">ID: {review.item_id}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-body text-sm text-secondary">{review.customer_name}</p>
                        {review.is_verified && (
                          <span className="font-body text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle size={12} /> Verified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={16}
                            weight={review.rating >= star ? 'fill' : 'regular'}
                            className={review.rating >= star ? 'text-primary' : 'text-gray-300'}
                          />
                        ))}
                        <span className="font-body text-sm text-secondary ml-1">{review.rating}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="max-w-xs">
                        {review.title && (
                          <p className="font-body text-sm font-medium text-secondary truncate">{review.title}</p>
                        )}
                        <p className="font-body text-xs text-text-muted truncate">{review.comment}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      {getStatusBadge(review)}
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-text-secondary">
                        {formatDate(review.created_at)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {!review.is_approved && (
                          <>
                            <button
                              onClick={() => handleApprove(review.id)}
                              className="p-2 text-text-muted hover:text-green-500 transition-colors"
                              title="Approve"
                            >
                              <Check size={18} />
                            </button>
                            <button
                              onClick={() => handleReject(review.id)}
                              className="p-2 text-text-muted hover:text-red-500 transition-colors"
                              title="Reject"
                            >
                              <X size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(review.id)}
                          className="p-2 text-text-muted hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border-default">
            {[...Array(pagination.totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setPagination(p => ({ ...p, page: i + 1 }))}
                className={`w-8 h-8 font-body text-sm transition-colors ${
                  pagination.page === i + 1 
                    ? 'bg-primary text-white' 
                    : 'bg-background-subtle text-secondary hover:bg-primary/10'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
